import { __awaiter, __rest } from "tslib";
import React, { useEffect, useState, useCallback } from 'react';
import { default as asyncCatch } from "@pb/utils/common/asyncCatch";
import { default as initializeMapsAPI } from "@pb/utils/maps/initializeMapsAPI";
import { default as getPlaceSuggestions } from "@pb/utils/maps/getPlaceSuggestions";
import { default as getStoreSuggestionsUtils } from "@pb/utils/maps/getStoreSuggestions";
import { default as getPlaceDetailsUtils } from "@pb/utils/maps/getPlaceDetails";
import cn from 'classnames';
import debounce from 'lodash.debounce';
import { useFormContext } from 'react-hook-form';
import SuggestionsList from "../../SuggestionsList";
import Input from "../../../ui/Fields/Input";
import { AddressFormat } from '../types';
import styles from './AddressAutocomplete.module.scss';
const AddressAutocomplete = (_a) => {
    var { shouldShowStoreSuggestions = false, className, placeholder, label, onBlur, onChange, onAddressSelectionStart, onAddressSelectionFinished, onStoreSearchFinishedClick, onSearchResultFindInStore, country, locale, icon, footerCredit, doNotLoadMapsAPI = false, minSearchCharacter = 3, isFieldWithoutMap, addressFormat = AddressFormat.default, shouldFillInFullAddress = false, successfullSearch = '', locationNotMatchingText, isAutoSuggestionDisabled } = _a, props = __rest(_a, ["shouldShowStoreSuggestions", "className", "placeholder", "label", "onBlur", "onChange", "onAddressSelectionStart", "onAddressSelectionFinished", "onStoreSearchFinishedClick", "onSearchResultFindInStore", "country", "locale", "icon", "footerCredit", "doNotLoadMapsAPI", "minSearchCharacter", "isFieldWithoutMap", "addressFormat", "shouldFillInFullAddress", "successfullSearch", "locationNotMatchingText", "isAutoSuggestionDisabled"]);
    let watch;
    let setValue;
    const formContext = useFormContext();
    if (isFieldWithoutMap) {
        watch = formContext.watch;
        setValue = formContext.setValue;
    }
    const inputValue = isFieldWithoutMap ? watch('line1') : props.inputValue || '';
    const [addressesSuggestions, setAddressesSuggestions] = useState([]);
    const [shouldShowSuggestions, setShouldShowSuggestions] = useState(false);
    const [isUserTyping, setIsUserTyping] = useState(false);
    const [currentSessionToken, setCurrentSessionToken] = useState(null);
    const [shouldShowNoLocationMatch, setShouldShowNoLocationMatch] = useState(false);
    const [locationNotMatchingErrorText, setLocationNotMatchingErrorText] = useState('');
    const [isMapsAPILoaded, setIsMapsAPILoaded] = useState(doNotLoadMapsAPI);
    useEffect(() => {
        const loadMapsAPI = () => __awaiter(void 0, void 0, void 0, function* () {
            yield initializeMapsAPI();
            setIsMapsAPILoaded(true);
        });
        if (!doNotLoadMapsAPI) {
            loadMapsAPI();
        }
    }, [doNotLoadMapsAPI]);
    const getAddressSuggestions = (query) => __awaiter(void 0, void 0, void 0, function* () {
        const [error, { suggestions, sessionToken }] = yield asyncCatch(getPlaceSuggestions({ query, country }));
        if (error) {
            throw new Error(error);
        }
        setCurrentSessionToken(sessionToken);
        return suggestions;
    });
    const getStoreSuggestions = (query) => __awaiter(void 0, void 0, void 0, function* () {
        if (!shouldShowStoreSuggestions) {
            return [];
        }
        const suggestions = yield getStoreSuggestionsUtils({ query, country, locale });
        return suggestions.map((suggestion) => ({
            text: suggestion.address,
            id: suggestion.address,
            isStore: true,
            icon: 'HMLogoIcon',
            lat: suggestion.latitude,
            lng: suggestion.longitude,
        }));
    });
    const getSuggestions = (value) => __awaiter(void 0, void 0, void 0, function* () {
        if (!isMapsAPILoaded || value.length < minSearchCharacter) {
            setAddressesSuggestions([]);
            return;
        }
        const [resultAddresses, resultStores] = yield Promise.all([
            getAddressSuggestions(value),
            getStoreSuggestions(value),
        ]);
        if (!isUserTyping) {
            onSearchResultFindInStore === null || onSearchResultFindInStore === void 0 ? void 0 : onSearchResultFindInStore(`${resultStores.length} ${value}`, successfullSearch);
        }
        setAddressesSuggestions([...resultStores, ...resultAddresses]);
        setShouldShowSuggestions(true);
    });
    const debouncedGetSuggestions = useCallback(debounce(getSuggestions, 250), [
        isMapsAPILoaded,
        country,
    ]);
    const hideSuggestionsList = () => {
        setShouldShowSuggestions(false);
        setShouldShowNoLocationMatch(false);
    };
    const getPlaceDetails = (selectedSuggestionId) => __awaiter(void 0, void 0, void 0, function* () {
        const [error, placeDetails] = yield asyncCatch(getPlaceDetailsUtils({
            id: selectedSuggestionId,
            sessionToken: currentSessionToken,
        }));
        if (error) {
            throw new Error(error);
        }
        return placeDetails;
    });
    const populateFormFields = (selectedAddressSuggestion) => {
        const { buildingNumber = '', route = '', city = '', postalCode = '', province = '', houseName = '', district = '', } = selectedAddressSuggestion;
        const line1Value = addressFormat === AddressFormat.default
            ? `${route}${buildingNumber && ` ${buildingNumber}`}`
            : `${buildingNumber && `${buildingNumber} `}${route}`;
        if (shouldFillInFullAddress) {
            const valuesToFillIn = [line1Value, city, postalCode];
            const fullValueString = valuesToFillIn
                .filter((addressSuggestion) => addressSuggestion)
                .join(', ');
            setValue('line1', fullValueString, {
                shouldValidate: true,
                shouldDirty: true,
            });
        }
        else {
            setValue('line1', line1Value, {
                shouldValidate: true,
                shouldDirty: true,
            });
        }
        setValue('line2', houseName, { shouldValidate: true, shouldDirty: true });
        setValue('town', city, { shouldValidate: true, shouldDirty: true });
        setValue('postalCode', postalCode, { shouldValidate: true, shouldDirty: true });
        setValue('province', province, { shouldValidate: true, shouldDirty: true });
        setValue('district', district, { shouldValidate: true, shouldDirty: true });
    };
    const handleSelection = ({ text, selectedSuggestionID, lat, lng }) => __awaiter(void 0, void 0, void 0, function* () {
        setShouldShowNoLocationMatch(false);
        if (!selectedSuggestionID && isUserTyping) {
            return;
        }
        if (onAddressSelectionStart) {
            onAddressSelectionStart(text);
        }
        if (onAddressSelectionFinished) {
            onStoreSearchFinishedClick === null || onStoreSearchFinishedClick === void 0 ? void 0 : onStoreSearchFinishedClick(text);
            if (!lat || !lng) {
                const placeId = selectedSuggestionID;
                onAddressSelectionFinished({ text, place: yield getPlaceDetails(placeId) });
                return;
            }
            onAddressSelectionFinished({ text, place: { lat, lng } });
        }
        else if (isFieldWithoutMap) {
            const [error, placeDetails] = yield asyncCatch(getPlaceDetailsUtils({
                id: selectedSuggestionID,
                sessionToken: currentSessionToken,
            }));
            if (error) {
                throw new Error(error);
            }
            populateFormFields(placeDetails);
        }
        hideSuggestionsList();
        setIsUserTyping(false);
    });
    const handleChange = (value) => {
        setIsUserTyping(true);
        if (onChange) {
            onChange(value);
        }
        if (value.length > minSearchCharacter) {
            setLocationNotMatchingErrorText(locationNotMatchingText ? locationNotMatchingText === null || locationNotMatchingText === void 0 ? void 0 : locationNotMatchingText.replace('{0}', value) : '');
            setShouldShowSuggestions(true);
            setShouldShowNoLocationMatch(true);
        }
        else {
            setShouldShowSuggestions(false);
            setShouldShowNoLocationMatch(false);
        }
        debouncedGetSuggestions(value);
    };
    const handleKeyboardEvents = (event) => () => {
        const { key } = event;
        const trackedKeys = ['ArrowUp', 'ArrowDown', 'Enter'];
        if (shouldShowSuggestions && trackedKeys.includes(key)) {
            event.preventDefault();
        }
    };
    return (React.createElement("div", { className: cn(styles.container, className) },
        React.createElement(Input, Object.assign({ className: styles.input, role: "combobox", type: "text", name: "addressAutocomplete", "aria-autocomplete": "list", "aria-haspopup": "true", "aria-owns": "addressSuggestions", "aria-expanded": shouldShowSuggestions, placeholder: placeholder, onBlur: onBlur, onChange: handleChange, onKeyDown: handleKeyboardEvents, onInput: () => setIsUserTyping(true), label: label, value: inputValue, icon: icon, shouldDisableValidation: true, shouldShowValidationIcon: false, autoComplete: "off" }, props)),
        shouldShowSuggestions && !isAutoSuggestionDisabled && (React.createElement(SuggestionsList, { className: cn(styles.suggestionsList, !!label && styles.suggestionsListWithLabel), id: "addressSuggestions", suggestions: addressesSuggestions, onSuggestionConfirm: handleSelection, onClose: hideSuggestionsList, footerCredit: footerCredit, shouldShowNoLocationMatch: shouldShowNoLocationMatch, locationNotMatchingText: locationNotMatchingErrorText }))));
};
export default AddressAutocomplete;
//# sourceMappingURL=AddressAutocomplete.js.map